import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Icon, rovingKey } from '@platform/ui';
import type { ICreateKindMenuProps } from './ICreateKindMenuProps';

/**
 * «+ Створити ▾» панелі списку для типу з кількома видами записів (прототип v4 baNewMenu, таблиця бізнес-аналізу): акцентна кнопка
 * з шевроном відкриває меню видів (220px, рядки 32px, тінь); вибір створює запис цього виду. Відкрите з клавіатури — фокус на першому виді
 * (мишею — як у прототипі, без фокуса в меню); ↑ ↓ Home End між видами, Esc і Tab закривають і повертають фокус на кнопку; клік поза меню
 * закриває його.
 */
export function CreateKindMenu({ label, menuLabel, items }: ICreateKindMenuProps) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const byKeyboard = useRef(false);
  const entries = () => [...(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  useEffect(() => { if (open && byKeyboard.current) entries()[0]?.focus(); }, [open]);
  const close = () => { setOpen(false); button.current?.focus(); };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); close(); return; }
    const list = entries();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const next = at < 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp') ? 0 : rovingKey(e.key, at, list.length);
    if (next === null) return;
    e.preventDefault();
    list[next]?.focus();
  };
  return (
    <div className="lv-kinds" onKeyDown={onKeyDown}>
      <button ref={button} type="button" className="lv-create lv-kinds__btn" aria-haspopup="menu" aria-expanded={open}
        onClick={(e) => { byKeyboard.current = e.detail === 0; setOpen((v) => !v); }}>
        {label}<Icon name="chevron-down" size={12} strokeWidth={2.4} />
      </button>
      {open && (
        <>
          <div className="lv-kinds__overlay" aria-hidden="true" onClick={() => setOpen(false)} />
          <div ref={menu} role="menu" aria-label={menuLabel} className="lv-kinds__menu">
            {items.map((it) => (
              <button key={it.key} type="button" role="menuitem" className="lv-kinds__item" onClick={() => { setOpen(false); it.onSelect(); }}>{it.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
